import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronsUpDown, Check, Building2, Crown, Loader2, LogOut } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLiveRefresh } from "@/hooks/useLiveRefresh";
import { formatApiError } from "@/lib/constants";

const EASE = [0.22, 1, 0.36, 1];
export const HINT_KEY = "vektor:switch-back-hint";

// Callout under the switcher right after joining a new workspace; stays until "Got it".
function SwitchBackHint({ from, mobile, onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8, rotate: -2 }}
      animate={{ opacity: 1, y: 0, rotate: 0 }}
      transition={{ duration: 0.5, ease: EASE, delay: 0.3 }}
      className={`absolute z-[55] w-64 left-0 top-full mt-4`}
      role="status"
      data-testid="workspace-switch-hint"
    >
      <div className="rounded-2xl border border-white/10 bg-[#0A120D]/95 p-4 shadow-[0_24px_60px_-12px_rgba(0,0,0,0.85)] backdrop-blur-md">
        <p className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-[#00FF66]">
          <span className="h-px w-4 bg-[#00FF66]" /> Your workspaces
        </p>
        <p className="mt-2 text-sm font-bold leading-snug text-white">
          <span className="text-white/60">{from}</span> is still here.
        </p>
        <p className="mt-1 text-xs leading-relaxed text-white/50">Tap the workspace name to hop back anytime.</p>
        <button onClick={onClose} className="mt-3 inline-flex items-center gap-1 text-[11px] font-bold text-[#00FF66] transition-[gap] duration-200 hover:gap-2" data-testid="workspace-switch-hint-close">
          Got it <span aria-hidden="true">→</span>
        </button>
      </div>
    </motion.div>
  );
}

// Soft beacon beside the switcher while the hint is up.
const Beacon = () => (
  <span className="relative flex h-2 w-2 shrink-0" aria-hidden="true">
    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#00FF66] opacity-70" />
    <span className="relative inline-flex h-2 w-2 rounded-full bg-[#00FF66]" />
  </span>
);

export default function WorkspaceSwitcher({ variant = "desktop" }) {
  const { user, setUser, switchWorkspace, leaveWorkspace } = useAuth();
  const [memberships, setMemberships] = useState(null);
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(null);
  const [leaving, setLeaving] = useState(null);
  const [leaveBusy, setLeaveBusy] = useState(false);
  const ref = useRef(null);
  const [switchHint, setSwitchHint] = useState(() => localStorage.getItem(HINT_KEY));
  const closeHint = useCallback(() => { localStorage.removeItem(HINT_KEY); setSwitchHint(null); }, []);

  const load = useCallback(() => {
    api.get("/auth/memberships").then((res) => setMemberships(res.data)).catch(() => setMemberships([]));
  }, []);
  useEffect(load, [load]);
  useLiveRefresh(load, { interval: 30000 });

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const handleSwitch = async (ws) => {
    if (ws.active || switching) return;
    setSwitching(ws.workspace_id);
    try {
      await switchWorkspace(ws.workspace_id);
      toast.success(`Switched to ${ws.workspace_name}`);
      window.location.assign("/app");
    } catch (err) {
      toast.error(formatApiError(err));
      setSwitching(null);
    }
  };

  const handleLeave = async () => {
    if (!leaving || leaveBusy) return;
    setLeaveBusy(true);
    try {
      const res = await leaveWorkspace(leaving.workspace_id);
      toast.success(`You left ${leaving.workspace_name}`);
      if (res.next) {
        setUser(res.next);
        window.location.assign("/app");
      } else {
        setUser(false);
        window.location.assign("/");
      }
    } catch (err) {
      toast.error(formatApiError(err));
      setLeaveBusy(false);
      setLeaving(null);
    }
  };

  const multi = (memberships?.length || 0) > 1;
  const name = user.workspace_name || "Workspace";
  const otherUnread = (memberships || []).filter((w) => !w.active).reduce((s, w) => s + (w.unread || 0), 0);
  const hint = multi && switchHint && !open && (
    <SwitchBackHint from={switchHint} mobile={variant === "mobile"} onClose={closeHint} />
  );

  // Single workspace (or still loading): keep the original static label, no dropdown.
  if (!multi) {
    return name ? (
      <span
        className="mt-1 block truncate text-[10px] font-semibold uppercase tracking-widest text-white/35"
        data-testid="sidebar-workspace-name"
      >
        {name}
      </span>
    ) : null;
  }

  const menu = (
    <div
      className="absolute z-50 mt-2 w-60 overflow-hidden rounded-2xl border border-white/10 bg-[#0A120D] p-1.5 shadow-2xl shadow-black/60"
      data-testid="workspace-switcher-menu"
    >
      <p className="px-3 pb-1.5 pt-1 text-[9px] font-bold uppercase tracking-[0.2em] text-white/30">
        Your workspaces
      </p>
      {memberships.map((ws) => (
        <div
          key={ws.workspace_id}
          className={`group flex items-center gap-0.5 rounded-xl transition-colors duration-150 ${
            ws.active ? "bg-[#00FF66]/10" : "hover:bg-white/5"
          }`}
        >
          <button
            onClick={() => handleSwitch(ws)}
            disabled={!!switching}
            className="flex min-w-0 flex-1 items-center gap-2.5 rounded-xl px-3 py-2 text-left"
            data-testid={`workspace-switch-${ws.workspace_id}`}
          >
            <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-[10px] font-black ${
              ws.active ? "bg-[#00FF66] text-black" : "bg-white/10 text-white/60"
            }`}>
              {(ws.workspace_name || "Workspace").slice(0, 2).toUpperCase()}
            </span>
            <span className="min-w-0 flex-1">
              <span className="flex items-center gap-1.5">
                <span className={`truncate text-sm font-semibold ${ws.active ? "text-white" : "text-white/80"}`}>
                  {ws.workspace_name}
                </span>
                {ws.is_owner && <Crown className="h-3 w-3 shrink-0 text-[#00FF66]/70" />}
              </span>
              <span className="block text-[10px] uppercase tracking-widest text-white/35">{ws.role}</span>
            </span>
            {switching === ws.workspace_id ? (
              <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-[#00FF66]" />
            ) : ws.active ? (
              <Check className="h-3.5 w-3.5 shrink-0 text-[#00FF66]" />
            ) : ws.unread > 0 ? (
              <span
                className="flex h-5 min-w-[20px] shrink-0 items-center justify-center rounded-full bg-[#00FF66] px-1.5 text-[10px] font-black text-black"
                data-testid={`workspace-unread-${ws.workspace_id}`}
              >
                {ws.unread > 99 ? "99+" : ws.unread}
              </span>
            ) : null}
          </button>
          {!ws.is_owner && (
            <button
              onClick={(e) => { e.stopPropagation(); setLeaving(ws); setOpen(false); }}
              title={`Leave ${ws.workspace_name}`}
              aria-label={`Leave ${ws.workspace_name}`}
              className="mr-1 shrink-0 rounded-lg p-1.5 text-white/25 opacity-0 transition-colors duration-150 hover:bg-[#FF8B94]/15 hover:text-[#FF8B94] focus:opacity-100 group-hover:opacity-100"
              data-testid={`workspace-leave-${ws.workspace_id}`}
            >
              <LogOut className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      ))}
    </div>
  );

  const leaveDialog = createPortal(
    <AnimatePresence>
      {leaving && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
          onClick={() => !leaveBusy && setLeaving(null)}
          data-testid="workspace-leave-dialog"
        >
          <motion.div
            initial={{ opacity: 0, y: 18, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.97 }}
            transition={{ duration: 0.26, ease: EASE }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md overflow-hidden rounded-2xl border border-[#FF8B94]/30 bg-[#0d0f12] p-7 shadow-[0_40px_120px_-20px_rgba(0,0,0,0.9)]"
          >
            <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-[#FF8B94]/30 bg-[#FF8B94]/10">
              <LogOut className="h-5 w-5 text-[#FF8B94]" />
            </div>
            <h2 className="font-display text-2xl font-black tracking-tight">Leave {leaving.workspace_name}?</h2>
            <p className="mt-3 text-sm leading-relaxed text-white/55">
              You'll be removed from this workspace and unassigned from its tasks and projects. Your account and your
              other workspaces stay exactly as they are. You can rejoin later only if an admin invites you again.
            </p>
            <div className="mt-6 grid grid-cols-[1fr_auto] gap-2.5">
              <button
                onClick={handleLeave}
                disabled={leaveBusy}
                className="flex items-center justify-center gap-2 rounded-full bg-[#FF8B94] py-3 text-sm font-bold text-black transition-transform duration-200 hover:scale-[1.02] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100"
                data-testid="workspace-leave-confirm"
              >
                {leaveBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogOut className="h-4 w-4" />}
                {leaveBusy ? "Leaving…" : "Leave workspace"}
              </button>
              <button
                onClick={() => setLeaving(null)}
                disabled={leaveBusy}
                className="rounded-full border border-white/15 px-5 py-3 text-sm font-bold text-white/70 transition-colors duration-200 hover:border-white/30 hover:text-white disabled:opacity-50"
                data-testid="workspace-leave-cancel"
              >
                Cancel
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );

  if (variant === "mobile") {
    return (
      <div className="relative" ref={ref}>
        <button
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-white/60"
          data-testid="workspace-switcher-mobile"
        >
          <Building2 className="h-3 w-3" />
          <span className="max-w-[90px] truncate">{name}</span>
          <ChevronsUpDown className="h-3 w-3" />
          {otherUnread > 0 && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#00FF66]" data-testid="workspace-switcher-dot" />}
        </button>
        {open && <div className="absolute left-0">{menu}</div>}
        {hint}
        {leaveDialog}
      </div>
    );
  }

  return (
    <div className="relative mt-1" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-1 rounded-md text-left transition-colors duration-150 hover:text-white/60"
        data-testid="workspace-switcher"
      >
        <span className="truncate text-[10px] font-semibold uppercase tracking-widest text-white/35" data-testid="sidebar-workspace-name">
          {name}
        </span>
        <ChevronsUpDown className="h-3 w-3 shrink-0 text-white/30" />
        {hint ? <Beacon /> : otherUnread > 0 && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#00FF66]" data-testid="workspace-switcher-dot" />}
      </button>
      {open && menu}
      {hint}
      {leaveDialog}
    </div>
  );
}
